// Who can sign in, and who can change that.

import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../api.js";
import { useIsAdmin, useUser } from "../components/AuthGate.jsx";
import { EmptyState, ErrorNote, PageHead, Shell } from "../components/Shell.jsx";
import { Avatar } from "../ui/Avatar.jsx";
import { Menu } from "../ui/Menu.jsx";
import { ConfirmModal, FormModal } from "../ui/Modal.jsx";
import { TextField } from "../ui/Field.jsx";
import { PanelRowsSkeleton, Skeleton } from "../ui/Skeleton.jsx";
import { Plus, Shield, Warning } from "../ui/icons.jsx";
import { exactTime, relativeTime } from "../ui/format.js";

const PEOPLE_COLUMNS = { gridTemplateColumns: "32px minmax(0,2fr) 110px minmax(0,1fr) 40px" };
const LIST_COLUMNS = { gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr) 40px" };

export const AdminPage = () => {
  const isAdmin = useIsAdmin();
  const me = useUser();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [overview, users, guests, invites] = await Promise.all([
        api.adminOverview(),
        api.listUsers(),
        api.listAllowlist(),
        api.listInvites(),
      ]);
      setData({
        overview,
        users: users.users,
        guests: guests.allowlist,
        invites: invites.invites,
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const run = async (work) => {
    setBusy(true);
    setDialogError(null);
    try {
      await work();
      setDialog(null);
      await load();
    } catch (err) {
      setDialogError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // A member who types the URL gets the boards, not a locked door.
  if (!isAdmin) return <Navigate to="/" replace />;

  const loading = data === null;
  const users = data?.users ?? [];
  const admins = users.filter((u) => u.role === "admin").length;

  return (
    <Shell crumbs={[{ label: "Admin" }]}>
      <ErrorNote error={error} onDismiss={() => setError(null)} />
      <PageHead title="Admin" lede="Manage who can sign in and who can change access." />

      <div className="stat-strip" style={{ marginBottom: 32 }}>
        {["People", "Admins", "Guests", "Folders", "Boards"].map((label, i) => (
          <div className="stat" key={label}>
            <div className="k">{label}</div>
            <div className="v">
              {loading ? (
                <Skeleton width={34} height={26} radius={6} />
              ) : (
                [
                  data.overview.counts.people,
                  data.overview.counts.admins,
                  data.overview.counts.guests,
                  data.overview.counts.folders,
                  data.overview.counts.boards,
                ][i]
              )}
            </div>
          </div>
        ))}
      </div>

      <section className="section">
        <div className="panel">
          <div className="panel-head">
            <h3>
              <Shield style={{ width: 17, height: 17, color: "var(--accent)" }} />
              People
            </h3>
            <button
              className="btn small"
              onClick={() => setDialog({ kind: "invite" })}
              disabled={loading}
            >
              <Plus />
              Invite an admin
            </button>
          </div>

          {loading ? (
            <PanelRowsSkeleton />
          ) : users.length === 0 ? (
            <div className="panel-body">
              <p className="muted">Nobody has signed in yet.</p>
            </div>
          ) : (
            users.map((person) => (
              <div className="board-row" key={person.sub} style={PEOPLE_COLUMNS}>
                <Avatar user={person} size={30} />
                <div className="board-cell">
                  <div className="truncate" style={{ fontWeight: 550 }}>
                    {person.name}
                    {person.sub === me?.id ? <span className="faint small"> · you</span> : null}
                  </div>
                  <div className="board-sub">
                    <span className="truncate">{person.email}</span>
                    {person.access === "revoked" ? (
                      <span className="pill bad">
                        <span className="dot" />
                        revoked
                      </span>
                    ) : null}
                  </div>
                </div>
                <div>
                  <span className={person.role === "admin" ? "tag on" : "tag"}>
                    <span className="sq" />
                    {person.role ?? "member"}
                  </span>
                </div>
                <div className="when" title={exactTime(person.lastSeenAt)}>
                  {person.lastSeenAt ? `seen ${relativeTime(person.lastSeenAt)}` : "—"}
                </div>
                <Menu
                  label={`Actions for ${person.name}`}
                  items={[
                    person.role === "admin"
                      ? {
                          label: "Change to member",
                          disabled: admins <= 1,
                          onSelect: () => setDialog({ kind: "role", person, role: "member" }),
                        }
                      : {
                          label: "Make admin",
                          onSelect: () => setDialog({ kind: "role", person, role: "admin" }),
                        },
                  ]}
                />
              </div>
            ))
          )}

          {!loading && data.invites.length > 0 ? (
            <>
              <div className="list-label">Invited</div>
              {data.invites.map((invite) => (
                <div className="board-row" key={invite.email} style={LIST_COLUMNS}>
                  <div className="board-cell truncate" style={{ fontWeight: 550 }}>
                    {invite.email}
                  </div>
                  <div className="board-cell">
                    <span className="tag">
                      <span className="sq" />
                      admin on first sign-in
                    </span>
                  </div>
                  <div className="board-cell small faint truncate" title={exactTime(invite.invitedAt)}>
                    invited by {invite.invitedBy} {relativeTime(invite.invitedAt)}
                  </div>
                  <Menu
                    label={`Actions for ${invite.email}`}
                    items={[
                      {
                        label: "Withdraw invite",
                        danger: true,
                        onSelect: () => setDialog({ kind: "withdraw", invite }),
                      },
                    ]}
                  />
                </div>
              ))}
            </>
          ) : null}

          <div className="panel-note">
            {loading
              ? "Loading"
              : users.length === 0
                ? "Roles appear here once someone has signed in."
                : admins === 0
                  ? "No admins. See docs/deploy.md to set the first one."
                  : admins === 1
                    ? "One admin. Promote someone else before this one can be changed to a member."
                    : `${admins} admins. Admins manage guests and roles.`}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="panel">
          <div className="panel-head">
            <h3>Guests</h3>
            <button
              className="btn small"
              onClick={() => setDialog({ kind: "addGuest" })}
              disabled={loading}
            >
              <Plus />
              Add a guest
            </button>
          </div>

          <div className="panel-body" style={{ paddingBottom: 0 }}>
            <div className="banner warn">
              <Warning />
              <span>
                Guests can see and edit <strong>every board</strong>. Removing one ends their
                current session.
              </span>
            </div>
          </div>

          {loading ? (
            <PanelRowsSkeleton rows={1} />
          ) : data.guests.length === 0 ? (
            <div className="panel-body">
              <EmptyState
                title="No guests"
                hint={`Right now only ${(data.overview.allowedDomains ?? []).map((d) => `@${d}`).join(" and ")} accounts can sign in.`}
              />
            </div>
          ) : (
            data.guests.map((guest) => (
              <div className="board-row" key={guest.email} style={LIST_COLUMNS}>
                <div className="board-cell truncate" style={{ fontWeight: 550 }}>
                  {guest.email}
                </div>
                <div className="board-cell muted small truncate">{guest.note || "No note"}</div>
                <div className="board-cell small faint truncate" title={exactTime(guest.addedAt)}>
                  added by {guest.addedBy} {relativeTime(guest.addedAt)}
                </div>
                <Menu
                  label={`Actions for ${guest.email}`}
                  items={[
                    {
                      label: "Remove access",
                      danger: true,
                      onSelect: () => setDialog({ kind: "removeGuest", guest }),
                    },
                  ]}
                />
              </div>
            ))
          )}
        </div>
      </section>

      {dialog?.kind === "invite" ? (
        <InviteAdmin busy={busy} error={dialogError} onClose={() => setDialog(null)} onSubmit={run} />
      ) : null}

      {dialog?.kind === "withdraw" ? (
        <ConfirmModal
          title={`Withdraw the invite for ${dialog.invite.email}?`}
          description="They will sign in as a member instead."
          confirmLabel="Withdraw"
          busy={busy}
          error={dialogError}
          onClose={() => setDialog(null)}
          onConfirm={() => run(() => api.withdrawInvite(dialog.invite.email))}
        />
      ) : null}

      {dialog?.kind === "addGuest" ? (
        <AddGuest busy={busy} error={dialogError} onClose={() => setDialog(null)} onSubmit={run} />
      ) : null}

      {dialog?.kind === "removeGuest" ? (
        <ConfirmModal
          title={`Remove ${dialog.guest.email}?`}
          description="They lose access immediately, including their current session."
          confirmLabel="Remove access"
          busy={busy}
          error={dialogError}
          onClose={() => setDialog(null)}
          onConfirm={() => run(() => api.removeGuest(dialog.guest.email))}
        />
      ) : null}

      {dialog?.kind === "role" ? (
        <ConfirmModal
          title={
            dialog.role === "admin"
              ? `Make ${dialog.person.name} an admin?`
              : `Change ${dialog.person.name} to a member?`
          }
          description={
            dialog.role === "admin"
              ? "Admins manage guests and roles. Takes effect within a minute."
              : "They keep access to every board. They can no longer manage access."
          }
          confirmLabel={dialog.role === "admin" ? "Make admin" : "Change to member"}
          busy={busy}
          error={dialogError}
          onClose={() => setDialog(null)}
          onConfirm={() => run(() => api.setUserRole(dialog.person.sub, dialog.role))}
        />
      ) : null}
    </Shell>
  );
};

const InviteAdmin = ({ onSubmit, onClose, busy, error }) => {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");

  return (
    <FormModal
      title="Invite an admin"
      description="They become an admin the first time they sign in."
      submitLabel="Send invite"
      canSubmit={/.+@.+\..+/.test(email.trim())}
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={() => onSubmit(() => api.inviteAdmin(email.trim(), note.trim() || undefined))}
    >
      <TextField
        label="Google account"
        type="email"
        value={email}
        placeholder="name@onehivemind.ai"
        hint="No email is sent. They sign in as usual."
        onChange={(event) => setEmail(event.target.value)}
      />
      <TextField
        label="Note"
        value={note}
        maxLength={500}
        placeholder="Optional"
        onChange={(event) => setNote(event.target.value)}
      />
    </FormModal>
  );
};

const AddGuest = ({ onSubmit, onClose, busy, error }) => {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");

  return (
    <FormModal
      title="Add a guest"
      description="Someone outside the hivemind domains."
      submitLabel="Grant access"
      canSubmit={/.+@.+\..+/.test(email.trim())}
      busy={busy}
      error={error}
      note="They can see every board."
      onClose={onClose}
      onSubmit={() => onSubmit(() => api.addGuest(email.trim(), note.trim() || undefined))}
    >
      <TextField
        label="Google account"
        type="email"
        value={email}
        placeholder="designer@example.com"
        hint="Use the address they sign in to Google with."
        onChange={(event) => setEmail(event.target.value)}
      />
      <TextField
        label="Why they have access"
        value={note}
        maxLength={500}
        placeholder="ESP designer, until October"
        hint="Optional, but useful later."
        onChange={(event) => setNote(event.target.value)}
      />
    </FormModal>
  );
};
